
import { Modal } from 'bootstrap';
import { defineComponent, ref, watchEffect } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { SITE_PRODUCTS } from '../graphql/Queries';
import { Apollo, Notify } from '@/core/services';
import { ADD_SUBSCRIPTION_PRODUCT } from '../graphql/Mutations';
import { Actions } from '@/store/enums/StoreEnums';
import Table from '../../../components/Table/Table.vue';
import InnerLoader from '../../../components/InnerLoader.vue';

export default defineComponent({
    name: 'Show all products',
    components: { Table, InnerLoader },
    setup() {
        const modal: any = ref();
        const storeHandler = useStore();
        const loader = ref(false);
        const i18n = useI18n();
        const system_locale = ref();
        const number = ref([]) as Record<any, any>;
        const products = ref([]) as Record<any, any>;
        const editedUnitPrices = ref<number[]>([]);
        const cartLimits = ref([]) as Record<any, any>;
        const order_subscription_id = ref([]);
        const columns = ref([
            {
                label: '',
                key: 'image'
            },
            {
                label: 'message.PRODUCT_NAME',
                key: 'name'
            },
            {
                label: 'message.PRICE',
                key: 'price',
                textAlignment: 'end'
            },
            {
                label: 'message.QUANTITY',
                key: 'quantity'
            },
            {
                label: 'message.ACTIONS',
                key: 'actions',
                textAlignment: 'center'
            }
        ]);

        watchEffect(() => {
            system_locale.value = i18n.fallbackLocale.value;
        });

        const appInstance = storeHandler.getters.appInstance;

        const roundMoney = (n: number) => Math.round(n * 100) / 100;

        const parseSubscriptionUnitPrice = (raw: unknown): number => {
            if (raw === null || raw === undefined || raw === '') return 0;
            if (typeof raw === 'number' && Number.isFinite(raw)) return roundMoney(raw);
            const s = String(raw).trim().replace(/\s/g, '');
            const cleaned = s.replace(/[^\d.,-]/g, '');
            if (!cleaned) return 0;
            let norm = cleaned;
            if (norm.includes(',') && norm.includes('.')) {
                norm =
                    norm.lastIndexOf(',') > norm.lastIndexOf('.')
                        ? norm.replace(/\./g, '').replace(',', '.')
                        : norm.replace(/,/g, '');
            } else if (norm.includes(',')) {
                norm = norm.replace(',', '.');
            }
            const n = parseFloat(norm);
            return Number.isFinite(n) ? roundMoney(n) : 0;
        };

        const subscribedProductIdSet = (): Set<number> => {
            const sub = storeHandler.getters.getAllSubscription as { order_products?: { product_id?: unknown }[] } | undefined;
            const lines = sub?.order_products ?? [];
            const ids = lines
                .map(row => Number(row?.product_id))
                .filter((id): id is number => Number.isFinite(id));
            return new Set(ids);
        };

        const Products = id => {
            loader.value = true;
            Apollo.watchQuery({
                query: SITE_PRODUCTS,
                errorPolicy: 'all',
                fetchPolicy: 'network-only',
                nextFetchPolicy: 'cache-only',
                variables: {
                    order_subscription_id: id
                }
            }).subscribe(({ data, errors }) => {
                if (errors) {
                    loader.value = false;
                }
                loader.value = false;
                const response = JSON.parse(data.site_products) as { id?: unknown }[];
                const alreadySubscribed = subscribedProductIdSet();
                const filtered = response.filter(p => !alreadySubscribed.has(Number(p.id)));
                products.value = filtered;
                const nextPrices: number[] = [];
                products.value.forEach((ele, index) => {
                    if (ele.multiple_limit != null && ele.multiples != null) {
                        number.value[index] = ele.multiples;
                    } else {
                        number.value[index] = 1;
                    }
                    const raw = ele.special_price != null && ele.special_price !== '' ? ele.special_price : ele.price;
                    nextPrices.push(parseSubscriptionUnitPrice(raw));
                });
                editedUnitPrices.value = nextPrices;
            });
        };

        const cartLimit = (multipleLimit, multiple) => {
            cartLimits.value = [];
            const limit = multipleLimit / multiple;
            let res = 0;
            for (let i = 1; i <= limit; i++) {
                res = multiple + res;
                cartLimits.value.push(res);
            }
        };

        const show = id => {
            Products(id);
            order_subscription_id.value = id;
            modal.value = new Modal(document.getElementById('product_modal'));
            modal.value.show();
        };

        const AddProduct = async (index: number, productRow: Record<string, unknown>) => {
            const unitPrice = editedUnitPrices.value[index];
            const qty = number.value[index];
            if (!Number.isFinite(unitPrice) || unitPrice < 0) {
                Notify.error(i18n.t('message.FILL_ALL_MISSING_FIELDS'));
                return;
            }
            if (qty == null || Number(qty) < 1) {
                Notify.error(i18n.t('message.FILL_ALL_MISSING_FIELDS'));
                return;
            }
            try {
                loader.value = true;
                await Apollo.mutate({
                    mutation: ADD_SUBSCRIPTION_PRODUCT,
                    variables: {
                        product_id: productRow.id,
                        quantity: qty,
                        multiple: productRow.multiple_limit == null && productRow.multiples == null ? false : true,
                        order_subscription_id: order_subscription_id.value,
                        price: roundMoney(Number(unitPrice))
                    },
                    update: (store, { data: { subscription_product } }) => {
                        const response = JSON.parse(subscription_product);
                        storeHandler.dispatch(Actions.SET_SUBSCRIPTION, response);
                        modal.value.hide();
                        loader.value = false;
                        Notify.success(`${i18n.t('message.SUBSCRIPTION_PRODUCT_ADDED_SUCCESSFULLY')}`);
                    }
                });
            } catch ($e) {
                loader.value = false;
            }
        };

        const inquiryProduct = () => {
            appInstance.$messageBox
                .confirm(`${i18n.t('message.PRODUCT_UNAVAILABILITY_MESSAGE')}`, i18n.t('message.INFO'), {
                    confirmTextButton: i18n.t('message.OK'),
                    cancelTextButton: i18n.t('message.CANCEL'),
                    type: 'info'
                })
                .then(async () => {
                    loader.value = false;
                });
        };

        return {
            system_locale,
            products,
            number,
            editedUnitPrices,
            cartLimits,
            columns,
            loader,
            inquiryProduct,
            show,
            cartLimit,
            AddProduct
        };
    }
});
